<script lang="ts">
	import { goto } from '$app/navigation';
	import { resolve } from '$app/paths';
	import { page } from '$app/state';
	import CardsFloating from '$lib/pages/landingpage/ui/CardsFloating.svelte';
	import MarketingButton from '$lib/shared/ui/MarketingButton.svelte';
	import Copyright from '$lib/shared/ui/Copyright.svelte';
	import AvatarComponent from '$lib/shared/ui/AvatarComponent.svelte';
	import CrownLeaderboardLink from '$lib/shared/ui/CrownLeaderboardLink.svelte';
	import ThemeToggle from '$components/ThemeToggle.svelte';
	import PopupAccountBox from '$lib/features/user/ui/PopupAccountBox.svelte';
	import Popup from '$components/Popup.svelte';
	import Fa from 'svelte-fa';
	import { faBars } from '@fortawesome/free-solid-svg-icons';
	import smallLogoLightSrc from '$img/logo/supertip-s-black.avif';
	import smallLogoDarkSrc from '$img/logo/supertip-s-white.avif';
	import logoLightSrc from '$img/logo/supertip-revised-black.avif';
	import logoDarkSrc from '$img/logo/supertip-revised-white.avif';
	import lightLeft from '$img/light-left.avif';
	import lightRight from '$img/light-right.avif';
	import bgOvalLeft from '$img/bg-oval-left.avif';
	import bgOvalRight from '$img/bg-oval-right.avif';

	let { user, children } = $props();

	// Mobile nav: below `sm` the inline links collapse into this menu so the bar
	// stays roomy instead of cramming links + toggle + Sign Up onto one row.
	let mobileMenuOpen = $state(false);
	let menuTriggerEl = $state<HTMLDivElement | null>(null);
	let menuEl = $state<HTMLElement | null>(null);

	function closeMobileMenu() {
		mobileMenuOpen = false;
	}
	function handleMenuClickOutside(e: MouseEvent) {
		if (!mobileMenuOpen) return;
		const target = e.target as Node;
		if (menuTriggerEl?.contains(target) || menuEl?.contains(target)) return;
		mobileMenuOpen = false;
	}

	let btnClass = 'rounded-full px-2.5 py-1';
	// Nav links are full-height (44px) tap targets: padding + min-height live on the
	// <a> itself (not a wrapper), so the whole pill is the hit area, not just the text.
	let navLinkClass =
		'inline-flex min-h-11 items-center rounded-full px-3 whitespace-nowrap transition-colors hover:text-surface-950-50';
	// Footer nav links: pad to a comfortable tap height (text alone is too short).
	let footerLink = 'inline-block py-2';

	function isHashSelected(hash: string, currentHash: string): string {
		return isSelectedClasses(currentHash === hash);
	}
	function isSelectedClasses(selected: boolean) {
		return [
			navLinkClass,
			selected
				? ' preset-filled-surface-500 border border-secondary-100-900 bg-surface-500/30 text-surface-950-50'
				: ''
		].join(' ');
	}

	function shouldHandleNavigationClick(event: MouseEvent): boolean {
		return !(
			event.defaultPrevented ||
			event.button !== 0 ||
			event.metaKey ||
			event.ctrlKey ||
			event.shiftKey ||
			event.altKey
		);
	}

	function scrollToTop() {
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
	}

	async function scrollHome(event: MouseEvent) {
		if (!shouldHandleNavigationClick(event)) {
			return;
		}

		event.preventDefault();
		await goto(resolve('/'), { noScroll: true, keepFocus: true });
		scrollToTop();
	}

	async function scrollToSection(event: MouseEvent, id: string) {
		if (!shouldHandleNavigationClick(event)) {
			return;
		}

		event.preventDefault();
		await goto(resolve(`/#${id}`), { noScroll: true, keepFocus: true });

		const target = document.getElementById(id);
		if (!target) {
			return;
		}

		const headerOffset = document.getElementById('header')?.offsetHeight ?? 0;
		const topPadding = 24;
		const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - topPadding;
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		window.scrollTo({ top: Math.max(top, 0), behavior: prefersReducedMotion ? 'auto' : 'smooth' });
	}

	let featuresClass = $derived(isHashSelected('#features', page.url.hash));
	let howitworksClass = $derived(isHashSelected('#howitworks', page.url.hash));
	let faqClass = $derived(isHashSelected('#faq', page.url.hash));
	let leaderboardClass = $derived(isSelectedClasses(page.route.id === '/leaderboard'));

	let iconClasses = 'lg:max-h-10';
	let iconStyles = 'max-width: 2rem';
</script>

<svelte:window onclick={handleMenuClickOutside} />

<div class="grid grid-rows-[auto_1fr_auto]">
	<!-- Decorations -->
	{#if page.route.id === '/'}
		<div>
			<div
				class="pointer-events-none absolute -top-40 h-full w-full overflow-hidden bg-transparent"
			>
				<CardsFloating />
			</div>
		</div>
		<div class="pointer-events-none absolute top-32 left-16 h-[40vw] w-[40vw] sm:top-16">
			<img
				src={bgOvalLeft}
				alt=""
				draggable="false"
				class="pointer-events-none z-0 transform-none"
			/>
		</div>
		<div class="pointer-events-none absolute top-32 right-16 h-[40vw] w-[40vw] sm:top-16">
			<img
				src={bgOvalRight}
				alt=""
				draggable="false"
				class="pointer-events-none z-0 transform-none"
			/>
		</div>
	{/if}
	<div class="pointer-events-none absolute top-0 left-0 z-0 h-1/5 w-1/5">
		<img src={lightLeft} fetchpriority="high" alt="" class="" draggable="false" />
	</div>
	<div class="pointer-events-none absolute top-0 right-0 z-0 h-1/5 w-1/5">
		<img src={lightRight} fetchpriority="high" alt="" class="" draggable="false" />
	</div>

	<!-- Header -->
	<header id="header" class="sticky top-0 z-10">
		<div class="flex items-center justify-center pt-2 md:pt-4">
			<div
				class="flex h-11 w-[95%] flex-row items-center justify-between rounded-full border border-surface-100 bg-surface-50-950 lg:h-12 lg:w-4/5 2xl:w-3/5 dark:border-surface-800!"
			>
				<!-- Logo -->
				<div class="flex items-center gap-2">
					<div class="pl-0 lg:hidden dark:hidden {btnClass}">
						<a href={resolve('/')} onclick={scrollHome}
							><img
								src={smallLogoLightSrc}
								class={iconClasses}
								style={iconStyles}
								alt="small light logo"
							/></a
						>
					</div>
					<div class="hidden lg:block lg:dark:hidden {btnClass}">
						<a href={resolve('/')} onclick={scrollHome}
							><img src={logoLightSrc} class={iconClasses} alt="light logo" /></a
						>
					</div>

					<div class="hidden pl-0 dark:block lg:dark:hidden {btnClass}">
						<a href={resolve('/')} onclick={scrollHome}
							><img
								src={smallLogoDarkSrc}
								class={iconClasses}
								style={iconStyles}
								alt="small dark logo"
							/></a
						>
					</div>
					<div class="hidden lg:dark:block {btnClass}">
						<a href={resolve('/')} onclick={scrollHome}
							><img src={logoDarkSrc} class={iconClasses} alt="dark logo" /></a
						>
					</div>
				</div>

				<!-- Links (inline on sm+, collapsed into the hamburger menu below sm) -->
				<div
					class="hidden items-center gap-0.5 text-sm text-secondary-700 sm:flex sm:gap-1 lg:text-base dark:text-secondary-200"
				>
					<a
						href={resolve('/#features')}
						onclick={(event) => scrollToSection(event, 'features')}
						class={featuresClass}>Features</a
					>
					<a
						href={resolve('/#howitworks')}
						onclick={(event) => scrollToSection(event, 'howitworks')}
						class={howitworksClass}>How it Works</a
					>
					<a
						href={resolve('/#faq')}
						onclick={(event) => scrollToSection(event, 'faq')}
						class={faqClass}>FAQ</a
					>
					<a href={resolve('/leaderboard')} class={leaderboardClass}>Leaderboard</a>
					<a
						href="https://help.supertip.gg"
						target="_blank"
						rel="noopener noreferrer"
						class={navLinkClass}>Help</a
					>
				</div>

				<!-- Theme & login/profile -->
				<div class="flex items-center space-x-1 pr-2 lg:space-x-4">
					<!-- Mobile menu trigger (sm- only) -->
					<div class="sm:hidden" bind:this={menuTriggerEl}>
						<button
							type="button"
							onclick={() => (mobileMenuOpen = !mobileMenuOpen)}
							aria-label="Open navigation menu"
							aria-expanded={mobileMenuOpen}
							class="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full text-secondary-700 transition-colors hover:text-surface-950-50 dark:text-secondary-200"
						>
							<Fa icon={faBars} />
						</button>
						<Popup
							bind:open={mobileMenuOpen}
							triggerEl={menuTriggerEl}
							placement="bottom-end"
							offsetVal={10}
						>
							<nav
								bind:this={menuEl}
								class="flex w-52 flex-col gap-0.5 rounded-2xl border border-surface-100 bg-surface-50-950 p-2 text-secondary-700 shadow-xl dark:border-surface-800! dark:text-secondary-200"
							>
								<a
									href={resolve('/#features')}
									onclick={(event) => {
										closeMobileMenu();
										scrollToSection(event, 'features');
									}}
									class="rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-surface-200-800 hover:text-surface-950-50"
									>Features</a
								>
								<a
									href={resolve('/#howitworks')}
									onclick={(event) => {
										closeMobileMenu();
										scrollToSection(event, 'howitworks');
									}}
									class="rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-surface-200-800 hover:text-surface-950-50"
									>How it Works</a
								>
								<a
									href={resolve('/#faq')}
									onclick={(event) => {
										closeMobileMenu();
										scrollToSection(event, 'faq');
									}}
									class="rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-surface-200-800 hover:text-surface-950-50"
									>FAQ</a
								>
								<a
									href={resolve('/leaderboard')}
									onclick={closeMobileMenu}
									class="rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-surface-200-800 hover:text-surface-950-50"
									>Leaderboard</a
								>
								<a
									href="https://help.supertip.gg"
									target="_blank"
									rel="noopener noreferrer"
									onclick={closeMobileMenu}
									class="rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-surface-200-800 hover:text-surface-950-50"
									>Help</a
								>
							</nav>
						</Popup>
					</div>
					<ThemeToggle />
					<span class="desktop-only vr h-6 border-surface-800 opacity-40 dark:border-surface-100"
					></span>
					<div class="flex items-center">
						<!-- Keep the avatar inside the pill: the bar is h-11 until lg (h-12),
						     so cap the avatar at 32px until lg to avoid the sm:h-10 overflow. -->
						<AvatarComponent {user} classes="sm:h-8! sm:w-8! lg:h-10! lg:w-10!">
							{#snippet children(user)}
								<PopupAccountBox {user} />
							{/snippet}
						</AvatarComponent>
					</div>
				</div>
			</div>
		</div>
	</header>
	<!-- Main Content -->
	<main class="w-full space-y-4">
		{@render children?.()}
	</main>
	<!-- Footer -->
	<footer class="container mx-auto max-w-screen-2xl p-6 md:w-3/4">
		<div class="grid grid-cols-1 gap-20 pb-10 lg:grid-cols-2">
			<div
				class="flex flex-col items-center justify-center space-y-5 rounded-lg border border-primary-400 p-10"
			>
				<p class="text-lg font-bold">
					Join thousands of streamers building stronger communities every day!
				</p>
				<div class="w-full">
					<MarketingButton href="/signin" variant="primary" showArrow={false} classes="w-full">
						Get started for free
					</MarketingButton>
				</div>
			</div>
			<div class="grid grid-cols-3">
				<div class="flex flex-col items-start space-y-1">
					<div class="pb-5 text-xl">Product</div>
					<a
						href={resolve('/#features')}
						onclick={(event) => scrollToSection(event, 'features')}
						class={footerLink}>Features</a
					>
					<a
						href={resolve('/#howitworks')}
						onclick={(event) => scrollToSection(event, 'howitworks')}
						class={footerLink}>How it Works</a
					>
				</div>
				<div class="flex flex-col items-start space-y-1">
					<div class="pb-5 text-xl">Company</div>
					<a
						href={resolve('/#faq')}
						onclick={(event) => scrollToSection(event, 'faq')}
						class={footerLink}>FAQ</a
					>
					<a
						href="https://help.supertip.gg"
						target="_blank"
						rel="noopener noreferrer"
						class={footerLink}>Knowledge Base</a
					>
					<a href="mailto:info@supertip.gg" class={footerLink}>Contact</a>
				</div>
				<div class="flex flex-col items-start space-y-1">
					<div class="pb-5 text-xl">Legal</div>
					<a href={resolve('/submission')} class={footerLink}>Submission Terms</a>
					<a href={resolve('/terms')} class={footerLink}>Terms of Service</a>
					<a href={resolve('/privacy')} class={footerLink}>Privacy Policy</a>
				</div>
			</div>
		</div>
		<div class="flex flex-col items-center justify-center py-5">
			<CrownLeaderboardLink />
			<Copyright />
		</div>
	</footer>
</div>
